import PropTypes from 'prop-types';
import { components as tokenKeys } from '@telus-uds/system-theme-tokens';

// Tokens can be primitive values (e.g. `'rgba(0,0,0,0'`, `12`), or objects of such values,
// such as tokens that describe shadow (e.g. shadow: { inset: true, color: 'rgba(...)' ... }),
// or components (e.g. Icon tokens)
const tokenValue = PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.elementType]);
const tokenValueType = PropTypes.oneOfType([tokenValue, PropTypes.objectOf(tokenValue)]);
export const getTokenNames = componentName => {
  const componentTokenSchema = tokenKeys[componentName];
  if (!componentTokenSchema) {
    throw new Error(`No "${componentName}" tokenKeys in @telus-uds/system-theme-tokens`);
  }
  return Object.keys(componentTokenSchema);
};

/**
 * Returns the subset of a set of tokens that may be accepted by the `tokens` prop of a named component
 * or by a provided array of tokens. A prefix may be provided, for example:
 *
 * @example
 * ```jsx
 * // returns tokens from `themeTokens` that exist in the theme schema for `Button`.
 * selectTokens('Button', themeTokens)
 * ```
 *
 * @example
 * ```jsx
 * // returns `{ backgroundColor, width }` where the values of those keys come from
 * // the source object's `{ containerBackgroundColor, containerWidth }` properties.
 * selectTokens(['backgroundColor', 'width'], themeTokens, 'container')
 * ```
 *
 * @example
 * ```jsx
 * // returns tokens that are defined in the `Button` theme schema, from tokens with
 * // a prefix `'button'` e.g. a token `buttonBorderWidth` outputs as `borderWidth`.
 * selectTokens('Button', themeTokens, 'button')
 * ```
 *
 * @param {string[]|string} specifier - a name of a component in the theme schema, or an array of token names
 * @param {object} tokens - a source object of theme tokens
 * @param {string} [prefix] - if provided, matches keys in the source object with this as the first camelCase item
 * @returns {object} - subset of theme tokens
 */
export const selectTokens = (specifier, tokens, prefix) => {
  const tokenNames = typeof specifier === 'string' ? getTokenNames(specifier) : specifier;
  const filteredTokens = tokenNames.reduce((validTokens, key) => {
    const prefixedKey = prefix ? `${prefix}${key[0].toUpperCase()}${key.slice(1)}` : key;
    const token = tokens[prefixedKey];
    return token !== undefined ? {
      ...validTokens,
      [key]: token
    } : validTokens;
  }, {});
  return filteredTokens;
};

/**
 * @typedef {string|number|boolean|{[key: string]:string|number|boolean}} TokenValue
 * @typedef {{[key: string]: TokenValue}} TokensSet
 * @typedef {(AppearanceSet) => TokensSet} TokensGetter
 * @typedef {TokensSet|TokensGetter} TokensProp
 */
/**
 * 'tokens' is an optional object prop on all themable components. Its keys must match the
 * token keys in the component's theme schema.
 *
 * This prop is intended to be used as an 'escape hatch' for difficult or exceptional cases
 * where the main theming system doesn't apply. It is intentionally permissive about values.
 *
 * @param {...string} componentsNames - one or more ComponentName, which tokens keys are accepted
 * @return {function} - a custom PropTypes validator
 *
 * @example
 * Component.propTypes = {
 *   // accepts all tokens keys defined in Component schema
 *   tokens: getTokensPropType('Component')
 * }
 *
 * Component.propTypes = {
 *   // accepts all tokens keys defined in schemas for Component1 and Component2
 *   tokens: getTokensPropType('Component1', 'Component2')
 * }
 */
export const getTokensPropType = function () {
  for (var _len = arguments.length, componentsNames = new Array(_len), _key = 0; _key < _len; _key++) {
    componentsNames[_key] = arguments[_key];
  }
  return (props, propName, componentName) => {
    PropTypes.checkPropTypes({
      [propName]: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
    }, props, propName, componentName);
    if (typeof props[propName] !== 'function') {
      PropTypes.checkPropTypes({
        [propName]: PropTypes.exact(Object.fromEntries(componentsNames.flatMap(component => getTokenNames(component).map(key => [key, tokenValueType]))))
      }, props, propName, componentName);
    }
  };
};

/**
 * Get a proptypes validator for a set of tokens that is not based on a component in the theme schema.
 *
 * For example, for a set of tokens used in a common style, or for a set of tokens required by
 * a themeless component whose tokens are set by a parent but requires tokens of a certain shape.
 *
 * By default, requires an object with a complete set of tokens (allowing `null` and `undefined`).
 *
 * @param {string[]} componentTokenKeys - array of strings of token names
 * @param {object} [options]
 * @param {boolean} [options.partial] - if true, allows tokens to be undefined
 * @param {boolean} [options.allowFunction] - if true, allows functions as well as tokens objects
 * @returns
 */
export const getTokensSetPropType = function (componentTokenKeys) {
  let {
    partial = false,
    allowFunction = false
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const tokensObjectValidator = PropTypes.exact(Object.fromEntries(componentTokenKeys.map(key => [key, partial ? tokenValueType :
  // Some theme tokens can validly resolve to `null`, but .isRequired treats null as undefined
  function (props, propName) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
      args[_key2 - 2] = arguments[_key2];
    }
    return props[propName] !== null && props[propName] !== undefined && tokenValueType.isRequired(props, propName, ...args);
  }])));
  return allowFunction ? PropTypes.oneOfType([tokensObjectValidator, PropTypes.func]) : tokensObjectValidator;
};